import { useEffect } from 'react';

const SITE_URL = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://padelvillas.com';

const faqItems = [
  {
    q: 'When does PadelVillas go live to guests?',
    a: 'PadelVillas is live and accepting founding partner villas now. We open fully to guests once our curated collection is established. Partners who join during this founding window are guaranteed priority placement from the moment guests arrive, ahead of every other villa.',
  },
  {
    q: 'Does my villa need a padel court to be listed?',
    a: 'Yes. A private, on-site padel court is the defining feature of every property on our platform. This is not a general luxury villa site. It is purpose-built for the padel lifestyle. Your court is your unique selling point.',
  },
  {
    q: 'What commission does PadelVillas charge?',
    a: 'Founding partners benefit from zero platform commission during our entire launch period. Future standard commission rates will be clearly communicated well in advance, and founding partners will always receive preferential terms. There are no hidden fees and no charges until a guest books.',
  },
  {
    q: 'What locations are you focusing on initially?',
    a: 'Our initial focus is on Mediterranean destinations, primarily Spain, Portugal, Italy, Greece, and South of France, which align closely with the high-net-worth padel audience we are targeting. We will expand geographically as the platform grows.',
  },
  {
    q: 'How does the coach booking feature work?',
    a: 'Guests staying at listed villas can book verified local padel coaches directly through PadelVillas. Coaches are vetted for quality and professionalism, creating an elevated experience for your guests and additional visibility for your property.',
  },
  {
    q: 'What does the onboarding process involve?',
    a: 'After registering your interest, our team will review your property and reach out personally. We then work with you to create a premium villa profile, including copy review, photography guidance, and court feature highlights.',
  },
  {
    q: 'Can I list a villa that is managed by an agency?',
    a: 'Absolutely. We welcome both owner-managed and agency-managed properties. If your villa is professionally managed, we will work with you and your management team to ensure the listing is accurate and up to date.',
  },
  {
    q: 'How long will founding partner benefits last?',
    a: 'Founding partner status is permanent. Priority placement and your founding partner badge are yours for the lifetime of your listing on PadelVillas. Zero commission applies throughout our full launch period, which we expect to run for a minimum of 12 months.',
  },
];

const schemas = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}/#webpage`,
    url: SITE_URL,
    name: 'List Your Luxury Villa with Private Padel Court | PadelVillas',
    description:
      "PadelVillas is the Mediterranean's first curated collection of luxury villas with private padel courts. List your villa and reach high-net-worth padel enthusiasts.",
    isPartOf: { '@id': `${SITE_URL}/#website` },
    inLanguage: 'en-GB',
    datePublished: '2025-01-01',
    dateModified: '2026-03-30', // last audit: 2026-03-30
    breadcrumb: { '@id': `${SITE_URL}/#breadcrumb` },
    potentialAction: [
      {
        '@type': 'ReadAction',
        target: [SITE_URL],
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${SITE_URL}/#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL,
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: 'PadelVillas',
    url: SITE_URL,
    description:
      "The Mediterranean's first curated collection of luxury villas with private padel courts.",
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/?s={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: 'PadelVillas',
    url: SITE_URL,
    email: 'partnerships@padelvillas.vip',
    logo: {
      '@type': 'ImageObject',
      url: 'https://public.readdy.ai/ai/img_res/7239570f-f58b-4ff6-9525-50b66fd9d5f3.png',
      width: 400,
      height: 100,
    },
    description:
      'Luxury villa listing platform connecting high-end villa owners with padel-playing travellers across the Mediterranean.',
    areaServed: [
      { '@type': 'Country', name: 'Spain' },
      { '@type': 'Country', name: 'Portugal' },
      { '@type': 'Country', name: 'Italy' },
      { '@type': 'Country', name: 'Greece' },
      { '@type': 'Country', name: 'France' },
    ],
    sameAs: [],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Luxury Villa Listing with Private Padel Court',
    serviceType: 'Luxury Villa Rental Platform',
    provider: {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
    },
    areaServed: 'Mediterranean',
    description:
      "List your luxury villa with a private padel court on the Mediterranean's first curated padel villa platform and connect with high-net-worth padel enthusiasts.",
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'EUR',
      description: 'Zero commission during the founding partner launch period.',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  },
];

export default function HomeSeo() {
  useEffect(() => {
    const ids: string[] = schemas.map((schema, i) => {
      const id = `schema-ld-home-${i}`;
      let el = document.getElementById(id) as HTMLScriptElement | null;
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        el.type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(schema, null, 2);
      return id;
    });

    return () => {
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el) document.head.removeChild(el);
      });
    };
  }, []);

  return null;
}
